// Crear en la tienda un producto que sólo existe en Atlas — fase B de
// ADR-2026-09-21-crear-el-producto-en-la-tienda-desde-el-chat.
//
// El caso que lo pidió: la persona genera una foto de algo que todavía no cargó en
// Tiendanube y dice "creámelo allá, que se llame Remera negra oversize, a 45.000 con 10
// de stock". Hasta acá el agente sabía traer y subir fotos, pero no crear: el camino era
// ir a Tiendanube, cargarlo a mano y recién después volver.
//
// El backend ya sabe hacerlo (`action: 'create'`): crea el producto, le sube las fotos
// en la misma operación y le guarda el vínculo al producto de Atlas. Lo que falta es
// todo lo que hay que cuidar ANTES de llamarlo, que es lo que vive en este archivo.
//
// **Tres cosas que no se deshacen desde Atlas, y por eso pesan:**
//  1. El producto queda **visible para sus clientes** apenas se crea. Tiendanube publica
//     lo que se crea; no hay campo de borrador en nuestro contrato de export.
//  2. **Sin precio se publicaría en $0.** El backend no lo exige (default "0"), así que
//     el freno tiene que estar acá: un producto a cero pesos a la vista es peor que no
//     crear nada.
//  3. Crear dos veces deja **dos productos** en la tienda, y se borran a mano allá.
import type { HerramientaChat } from './tipos';
import type { TnProduct } from '../lib/tiendaNube';
import {
  TOPE_FOTOS_POR_PRODUCTO,
  elegirProductoDeAtlas,
  mensajeDeVarios,
  type ImagenGenerada,
  type ProductoDeAtlas,
} from './tiendanubeEnElChat';
import { anotarAviso, consumirAviso, puedeActuar } from './confirmacionVerificable';

export const NOMBRE_CREAR_PRODUCTO = 'crear_producto_en_la_tienda';

export const HERRAMIENTA_CREAR_PRODUCTO: HerramientaChat = {
  name: NOMBRE_CREAR_PRODUCTO,
  description:
    'Crea en la Tiendanube del usuario un producto que hoy sólo existe en Atlas, con las fotos que generaste. Usala cuando pida crear, cargar o publicar un producto nuevo en su tienda. `producto` es el de Atlas (por id o nombre) del que salen las fotos y al que queda vinculado. El PRECIO es obligatorio: sin él no creo nada, porque se publicaría en $0 a la vista de sus clientes. Queda visible en la tienda apenas se crea y no se deshace desde Atlas: la primera llamada te devuelve el aviso y NO crea nada; contale qué se va a crear, esperá su respuesta, y recién en tu turno siguiente volvé a llamarla con confirmado. Si el producto ya vino de Tiendanube no la uses: para esos es subir_fotos_a_la_tienda.',
  input_schema: {
    type: 'object',
    properties: {
      producto: {
        type: 'string',
        description: 'El producto de Atlas del que salen las fotos, por id o por nombre.',
      },
      nombre: {
        type: 'string',
        description:
          'Con qué nombre se crea en la tienda. Si no lo decís, va el nombre que tiene en Atlas.',
      },
      precio: { type: 'string', description: 'OBLIGATORIO. Sólo el número, sin símbolos.' },
      stock: { type: 'string', description: 'Cuántas unidades. Si no lo decís, va sin stock.' },
      sku: { type: 'string', description: 'El código del producto, si lo tiene.' },
      descripcion: { type: 'string', description: 'La descripción para la tienda.' },
      imagenes: {
        type: 'array',
        description: 'Los image_id de las fotos que van a la galería, en orden.',
        items: { type: 'string' },
      },
      confirmado: {
        type: 'boolean',
        description:
          'Sólo true después de que el usuario dijo que sí, en un mensaje posterior al aviso.',
      },
    },
    required: ['producto', 'precio'],
  },
};

/**
 * Lo que ya se mandó a crear en esta pestaña.
 *
 * Igual que con las fotos: crear es fire-and-forget, el vínculo lo escribe el worker
 * cuando Tiendanube contesta, y entre dos llamadas seguidas del modelo la base todavía
 * no se enteró. Sin esta red, un "dale, creálo" repetido deja dos productos en la tienda
 * del comerciante.
 */
const creadosRecientes = new Set<string>();

export function recordarCreacion(productoId: string): void {
  creadosRecientes.add(productoId);
}

export function yaSeCreoReciente(productoId: string): boolean {
  return creadosRecientes.has(productoId);
}

/** Para los tests: cada caso arranca sin memoria. */
export function olvidarCreaciones(): void {
  creadosRecientes.clear();
}

const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * El precio, en el ÚNICO formato que puede viajar: número con punto decimal, o `null` si
 * no sirve.
 *
 * Se acepta cómo lo dicta la persona —"45.000", "45,50", "$ 45000"— porque el modelo
 * copia lo que escuchó. Pero lo que se manda es SIEMPRE lo normalizado: validar una copia
 * limpia y mandar el original dejaba pasar "45.000" tal cual (Tiendanube lo lee como
 * cuarenta y cinco) y "-100", porque el signo se sacaba sólo para validar. Un producto a
 * la venta con el precio equivocado es de las peores cosas que puede hacer esta
 * herramienta (review Greptile #127).
 *
 * Reglas del formato: el punto es separador de miles sólo si NO queda como decimal (dos
 * dígitos al final tras una coma), que es como se escribe en la Argentina.
 */
export function precioCanonico(precio: string): string | null {
  const crudo = precio.trim();
  if (!crudo) return null;
  // Un signo menos en cualquier lado descalifica: no existe el precio negativo.
  if (crudo.includes('-')) return null;
  const soloNumeros = crudo.replace(/[^0-9.,]/g, '');
  if (!soloNumeros) return null;
  // Con coma, la coma manda como decimal y los puntos son miles ("45.000,50").
  // Sin coma, un punto con exactamente dos decimales es decimal ("45.50"); si no, miles.
  let normalizado: string;
  if (soloNumeros.includes(',')) {
    normalizado = soloNumeros.replace(/\./g, '').replace(',', '.');
  } else {
    const partes = soloNumeros.split('.');
    normalizado =
      partes.length === 2 && partes[1]!.length <= 2 ? soloNumeros : partes.join('');
  }
  const n = Number(normalizado);
  if (!Number.isFinite(n) || n <= 0) return null;
  // Sin ceros de más ni notación rara: lo que se manda es lo que se muestra.
  return String(n);
}

/** ¿Ese precio se puede usar? Es `precioCanonico` mirado como sí o no. */
export const precioValido = (precio: string): boolean => precioCanonico(precio) !== null;

/**
 * Productos de la tienda que se parecen al nombre que se va a crear.
 *
 * No bloquea: sirve para que la persona vea, ANTES de confirmar, que capaz ya lo tiene
 * allá con otro nombre. Crear el duplicado se arregla entrando a Tiendanube a borrarlo.
 */
export function parecidosEnLaTienda(nombre: string, catalogo: TnProduct[]): TnProduct[] {
  const buscado = normalizar(nombre);
  if (!buscado) return [];
  return catalogo.filter((p) => {
    const suyo = normalizar(p.nombre);
    return suyo === buscado || suyo.includes(buscado) || buscado.includes(suyo);
  });
}

export interface DependenciasDeCrear {
  productosDeAtlas: () => Promise<ProductoDeAtlas[]>;
  imagenes: (ids: string[]) => Promise<ImagenGenerada[]>;
  /** El catálogo de la tienda, para avisar si ya hay uno parecido. */
  catalogoDeLaTienda: () => Promise<TnProduct[]>;
  /** Manda a crear. `productos: 0` = el backend no lo aceptó y no va a pasar nada. */
  crear: (args: {
    productoId: string;
    nuevo: { nombre: string; precio: string; descripcion: string; stock: string; sku: string };
    imagenes: ImagenGenerada[];
  }) => Promise<{ productos: number; imagenes: number; motivo?: string }>;
}

const comoTexto = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

const idsDeImagenes = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((i): i is string => typeof i === 'string' && i.trim().length > 0) : [];

export async function resolverCrearProducto(
  input: unknown,
  deps: DependenciasDeCrear,
  ctx: { turno: number | null; ultimoMensaje?: string },
): Promise<{ __texto: string }> {
  const texto = (t: string) => ({ __texto: t });
  const pedido = (input ?? {}) as Record<string, unknown>;

  const nombrePedido = comoTexto(pedido.producto);
  if (!nombrePedido) return texto('Falta decir de qué producto de Atlas salen las fotos.');

  const eleccion = elegirProductoDeAtlas(nombrePedido, await deps.productosDeAtlas());
  if (eleccion.tipo === 'varios') return texto(mensajeDeVarios(nombrePedido, eleccion.opciones));
  if (eleccion.tipo === 'ninguno') {
    return texto(
      `No encontré "${nombrePedido}" entre los productos de Atlas. Preguntale a cuál se refiere.`,
    );
  }
  const producto = eleccion.producto;

  // Ya está en la tienda: crear otro sería duplicarlo, y encima el vínculo quedaría
  // apuntando al nuevo.
  if (producto.tnProductId !== null) {
    return texto(
      `"${producto.nombre}" YA existe en su tienda, así que no lo creo de nuevo. Si quiere sumarle estas fotos, usá subir_fotos_a_la_tienda; si quiere cambiarle el precio o los datos, cambiar_datos_en_la_tienda.`,
    );
  }

  if (yaSeCreoReciente(producto.id)) {
    return texto(
      `Ya lo mandé a crear recién y no lo repito: dos llamadas seguidas dejarían DOS productos en su tienda. Decile que se está creando y que puede tardar un rato en aparecer.`,
    );
  }

  const precio = precioCanonico(comoTexto(pedido.precio)) ?? '';
  if (!precio) {
    return texto(
      'No creé nada porque falta el precio (o no es un número). Sin precio, el producto se publicaría en $0 y sus clientes lo verían así. Preguntale a cuánto lo vende y volvé a llamarme.',
    );
  }

  const nombreEnLaTienda = comoTexto(pedido.nombre) || producto.nombre;
  const stock = comoTexto(pedido.stock);
  const sku = comoTexto(pedido.sku);
  const descripcion = comoTexto(pedido.descripcion);

  const pedidas = idsDeImagenes(pedido.imagenes);
  const encontradas = pedidas.length > 0 ? await deps.imagenes(pedidas) : [];
  const perdidas = pedidas.length - encontradas.length;
  // El endpoint exige al menos una foto y admite hasta 40, y valida el pedido ENTERO:
  // sin fotos rechaza la creación, y con 41 no crea nada. Las dos cosas se atajan acá,
  // donde todavía se le puede explicar a la persona.
  if (encontradas.length === 0) {
    return texto(
      pedidas.length === 0
        ? 'No creé nada: un producto no se puede crear sin ninguna foto. Preguntale cuál de las que generó quiere usar, o generá una primero.'
        : 'No creé nada: ninguna de esas fotos existe. Mostrale las últimas que generó y preguntá cuáles van.',
    );
  }
  const imagenes = encontradas.slice(0, TOPE_FOTOS_POR_PRODUCTO);
  const sobrantes = encontradas.length - imagenes.length;

  // La clave del permiso incluye TODO lo que se va a crear: si cambia el precio o el
  // nombre, el sí anterior no vale — es otro producto.
  const clave = [
    producto.id,
    nombreEnLaTienda,
    precio,
    stock,
    sku,
    descripcion,
    imagenes.map((i) => i.id).join(','),
  ].join('|');
  const confirmado = pedido.confirmado === true;

  if (
    !puedeActuar({
      accion: NOMBRE_CREAR_PRODUCTO,
      clave,
      confirmado,
      turno: ctx.turno,
      ultimoMensaje: ctx.ultimoMensaje,
    })
  ) {
    anotarAviso(NOMBRE_CREAR_PRODUCTO, clave, ctx.turno);
    const parecidos = parecidosEnLaTienda(nombreEnLaTienda, await deps.catalogoDeLaTienda());
    const aviso =
      parecidos.length > 0
        ? ` OJO: en su tienda ya hay ${parecidos.length === 1 ? 'uno que se llama parecido' : 'varios que se llaman parecido'} (${parecidos.slice(0, 5).map((p) => `"${p.nombre}"`).join(', ')}). Preguntale si no es el mismo: si lo es, mejor traelo con traer_productos_de_la_tienda y subile las fotos, en vez de crear otro.`
        : '';
    const conFotos = `con ${imagenes.length === 1 ? '1 foto' : `${imagenes.length} fotos`}`;
    const faltantes = [
      perdidas > 0 ? `${perdidas} de las que pasaste no existen` : '',
      sobrantes > 0 ? `entran hasta ${TOPE_FOTOS_POR_PRODUCTO} y ${sobrantes} quedan afuera` : '',
    ]
      .filter(Boolean)
      .join(', ');
    const aclaracion = faltantes ? ` (${faltantes})` : '';
    return texto(
      `NO CREÉ NADA TODAVÍA. Esto va a crear en su tienda "${nombreEnLaTienda}" a $${precio}${stock ? `, con ${stock} de stock` : ', sin stock cargado'}${sku ? `, SKU ${sku}` : ''}, ${conFotos}${aclaracion}. **Queda visible para sus clientes apenas se crea** y desde Atlas no se puede borrar: sacarlo es entrar a Tiendanube.${aviso} Decíselo con esas palabras y preguntale si está seguro. Si en su próximo mensaje dice que sí, volvé a llamarme con confirmado en true.`,
    );
  }

  // Igual que las demás: el permiso vale para ESTA creación.
  consumirAviso(NOMBRE_CREAR_PRODUCTO);
  const r = await deps.crear({
    productoId: producto.id,
    nuevo: { nombre: nombreEnLaTienda, precio, descripcion, stock, sku },
    imagenes,
  });

  if (r.productos === 0) {
    if (r.motivo === 'tienda_no_conectada') {
      return texto(
        'No se creó nada: la tienda dejó de estar conectada. Decíselo así y pedile que la vuelva a conectar desde Mi marca.',
      );
    }
    return texto(
      'No se pudo mandar a crear el producto. Decíselo sin inventar la causa, aclarando que en su tienda NO se creó nada, y ofrecé reintentar.',
    );
  }

  recordarCreacion(producto.id);
  const conFotos =
    r.imagenes === 0 ? 'sin fotos' : `con ${r.imagenes === 1 ? '1 foto' : `${r.imagenes} fotos`}`;
  return texto(
    `Lo estoy creando en su tienda: "${nombreEnLaTienda}" a $${precio}, ${conFotos}. NO digas que ya está: tarda un rato y lo va a ver aparecer en su panel de Tiendanube. Cuando aparezca queda vinculado a "${producto.nombre}" de Atlas, así que desde ahí podés subirle más fotos o cambiarle los datos sin preguntar de nuevo cuál es.`,
  );
}
